import { useCallback, type FunctionComponent, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { DynamicButton } from '@trello/dynamic-tokens';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';
import { CloseIcon } from '@trello/nachos/icons/close';
import type { GAModalTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { showGAAnnouncementSpotlight } from '../betaGASharedState';
import { useAssignPersonalProductivityCohort } from '../useAssignPersonalProductivityCohort';
import GAModalImagePngSrc from './beta-ga-modal-image.png';

import styles from './GAAnnouncementModalWithOptIn.less';

const MAY_21_DATE_VALUE = 1747803600000;

export const GAAnnouncementModalWithOptIn: FunctionComponent<{
  onClose: () => void;
}> = ({ onClose }) => {
  const intl = useIntl();
  const isBeforeMay21 = Date.now() < MAY_21_DATE_VALUE;
  const { assignPersonalProductivityBetaCohort } =
    useAssignPersonalProductivityCohort();

  const onClickClose = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'closeGAModalButton',
      source: 'gaAnnouncementModal',
    });
    onClose();
    showGAAnnouncementSpotlight();
  }, [onClose]);

  const onClickDismiss = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'dismissGAAnnouncementModalButton',
      source: 'gaAnnouncementModal',
    });
    onClose();
    showGAAnnouncementSpotlight();
  }, [onClose]);

  const onClickContinue = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'enableBetaExperienceButton',
      source: 'gaAnnouncementModal',
    });

    try {
      await assignPersonalProductivityBetaCohort('opted_in');
    } catch (error) {
      showFlag({
        id: 'ppOptInModalError',
        title: 'Error opting into Beta',
        appearance: 'error',
      });
    }

    onClose();
  }, [assignPersonalProductivityBetaCohort, onClose]);

  return (
    <div className={styles.gaAnnouncementModal}>
      <img className={styles.headingImage} src={GAModalImagePngSrc} alt="" />
      <DynamicButton
        className={styles.closeButton}
        iconBefore={<CloseIcon size="medium" />}
        onClick={onClickClose}
        aria-label={intl.formatMessage({
          id: 'templates.dialog_close_button.close-dialog-label',
          defaultMessage: 'Close dialog',
          description: 'Aria label for the close dialog button',
        })}
        data-testid={getTestId<GAModalTestIds>('ga-modal-close-button')}
      />
      <div className={styles.contentContainer}>
        <FormattedMessage
          id="templates.beta_ga.a-new-trello-is-coming"
          defaultMessage="A new Trello is coming to everyone soon"
          description="GA modal content"
          tagName="h1"
        />
        <p>
          {isBeforeMay21 && (
            <FormattedMessage
              id="templates.beta_ga.starting-may-21st"
              defaultMessage="Starting May 21st, we're gradually releasing the new Trello to everyone."
              description="GA modal content"
              tagName="span"
            />
          )}
          &nbsp;
          <FormattedMessage
            id="templates.beta_ga.with-new-features-like-inbox-and-planner"
            defaultMessage="With new features like <strong>Inbox</strong> and <strong>Planner</strong>, you can capture to-dos and schedule time to get things done."
            description="GA modal content"
            tagName="span"
            values={{
              strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
            }}
          />
        </p>
        <FormattedMessage
          id="templates.beta_ga.enjoy-full-access"
          defaultMessage="Enjoy full access to all features before Beta ends."
          description="GA modal content"
          tagName="p"
        />
        <div className={styles.ctasContainer}>
          <Button appearance="subtle" onClick={onClickDismiss}>
            <FormattedMessage
              id="templates.beta_ga.dismiss-modal-button"
              defaultMessage="Dismiss"
              description="GA modal dismiss button"
            />
          </Button>
          <Button appearance="primary" onClick={onClickContinue}>
            <FormattedMessage
              id="templates.beta_ga.enable-experience-button"
              defaultMessage="Enable the new experience"
              description="GA modal enable Beta button"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
